/**
 * Hotel Traveller Details Thunk
 *
 * Redux thunks for traveller operations (add/edit/select/deselect).
 * Backend is the single source of truth - frontend NEVER derives state locally.
 *
 * State Management Rule:
 * - Traveller state must be fully backend-controlled
 * - Frontend should never derive or compute traveller selection state
 * - Always override state with API response
 *
 * TEMPORARY: Added local fallback for when backend returns empty travellers.
 * This will be removed once the Python Agent properly returns traveller data.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { HotelSavedTraveller, TravellerDetailActionType } from '@contracts';
import {
  callTravellerDetailsApi,
  TravellerDetailsError,
  type TravellerDetailsRequestParams,
} from '../../api';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface TravellerOperationParams {
  /** Transaction key from review response */
  transactionKey: string;
  /** Traveller details for the operation */
  travellerDetails: TravellerDetailsRequestParams['travellerDetails'];
}

interface TravellerOperationResult {
  /** Updated list of all travellers (backend-controlled) */
  travellers: HotelSavedTraveller[];
}

interface SelectTravellerParams {
  /** Transaction key from review response */
  transactionKey: string;
  /** Traveller ID to select/deselect */
  travellerId: string;
  /** Whether to select (true) or deselect (false) */
  selected: boolean;
  /** Optional room number to assign (for lead pax mode) */
  roomNumber?: number;
}

// ============================================
// TEMPORARY: Local Fallback for Empty Backend Response
// ============================================
// This will be removed once the Python Agent properly returns traveller data.

/**
 * Generate a unique traveller ID based on existing traveller count.
 * Format: Sequential number as string (e.g., "1", "2", "3")
 */
function generateSequentialTravellerId(existingCount: number, index: number): string {
  return String(existingCount + index + 1);
}

/**
 * Generate travellers locally from the request details.
 * Used as fallback when backend returns empty travellers array.
 */
function generateLocalTravellers(
  travellerDetails: TravellerOperationParams['travellerDetails'],
  existingTravellers: HotelSavedTraveller[],
  actionType: TravellerDetailActionType,
): HotelSavedTraveller[] {
  // For add operation - merge new travellers with existing
  if (actionType === 'hotel_traveller_detail_add') {
    const existingCount = existingTravellers.length;
    const newTravellers: HotelSavedTraveller[] = travellerDetails.map((t, index) => ({
      travellerId: t.travellerId ?? generateSequentialTravellerId(existingCount, index),
      salutation: t.salutation,
      firstName: t.firstName,
      lastName: t.lastName,
      isPrimary: t.isPrimary,
      emailId: t.emailId ?? '',
      contactInfo: t.contactInfo ?? '',
      roomNumber: t.roomNumber ?? 0,
      // Auto-select primary traveller and room-assigned travellers (for room-wise mode)
      isSelected: t.isSelected ?? t.isPrimary ?? (t.roomNumber && t.roomNumber > 0) ?? false,
    }));
    return [...existingTravellers, ...newTravellers];
  }

  // For edit operation - update matching traveller
  if (actionType === 'hotel_traveller_detail_edit') {
    const editedTraveller = travellerDetails[0];
    if (!editedTraveller) return existingTravellers;

    return existingTravellers.map((t) => {
      if (t.travellerId === editedTraveller.travellerId) {
        return {
          ...t,
          salutation: editedTraveller.salutation,
          firstName: editedTraveller.firstName,
          lastName: editedTraveller.lastName,
          isPrimary: editedTraveller.isPrimary,
          emailId: editedTraveller.emailId ?? t.emailId,
          contactInfo: editedTraveller.contactInfo ?? t.contactInfo,
          roomNumber: editedTraveller.roomNumber ?? t.roomNumber,
        };
      }
      return t;
    });
  }

  // For select/deselect - update isSelected flag
  if (
    actionType === 'hotel_traveller_detail_select' ||
    actionType === 'hotel_traveller_detail_deselect'
  ) {
    const targetTraveller = travellerDetails[0];
    if (!targetTraveller) return existingTravellers;

    return existingTravellers.map((t) => {
      if (t.travellerId === targetTraveller.travellerId) {
        return { ...t, isSelected: targetTraveller.isSelected ?? false };
      }
      return t;
    });
  }

  return existingTravellers;
}

// ============================================
// Generic Thunk Creator
// ============================================

function createTravellerThunk(name: string, actionType: TravellerDetailActionType) {
  return createAsyncThunk<
    TravellerOperationResult,
    TravellerOperationParams,
    { state: RootState; dispatch: AppDispatch; rejectValue: string }
  >(
    `hotels/travellers/${name}`,
    async (params, { getState, rejectWithValue }) => {
      try {
        const sessionId = getOrCreateSessionId();
        const userId = getUUID();
        const state = getState();
        const existingTravellers = state.hotels.bookingReview.travellers;

        logger.info(`[TravellerThunk:${name}] Starting operation`, {
          travellerCount: params.travellerDetails.length,
          travellerIds: params.travellerDetails.map((t) => t.travellerId),
          existingTravellerCount: existingTravellers.length,
        });

        const response = await callTravellerDetailsApi({
          sessionId,
          userId,
          transactionKey: params.transactionKey,
          actionType,
          travellerDetails: params.travellerDetails,
        });

        if (!response.success && response.error) {
          logger.warn(`[TravellerThunk:${name}] API error:`, response.error);
          return rejectWithValue(response.error.message);
        }

        // TEMPORARY FALLBACK: Handle cases where backend returns empty travellers
        // or travellers with missing travellerId fields.
        // This will be removed once the Python Agent properly returns traveller data.
        let travellers = response.travellers;

        // Check if backend returned empty OR if any traveller is missing travellerId
        const hasMissingIds = travellers.some((t) => !t.travellerId);

        if (travellers.length === 0 || hasMissingIds) {
          if (travellers.length === 0) {
            logger.warn(
              `[TravellerThunk:${name}] Backend returned empty travellers, using local fallback`,
            );
          } else {
            logger.warn(
              `[TravellerThunk:${name}] Backend returned travellers with missing IDs, using local fallback`,
            );
            logger.info(
              `[TravellerThunk:${name}] Backend travellers (with missing IDs):`,
              travellers,
            );
          }
          logger.info(`[TravellerThunk:${name}] Existing travellers:`, existingTravellers);
          logger.info(`[TravellerThunk:${name}] Request details:`, params.travellerDetails);
          travellers = generateLocalTravellers(
            params.travellerDetails,
            existingTravellers,
            actionType,
          );
          logger.info(`[TravellerThunk:${name}] Generated travellers:`, travellers);
        }

        logger.info(`[TravellerThunk:${name}] Success`, {
          travellerCount: travellers.length,
          travellers,
        });

        // Return the complete traveller list from backend
        // Frontend MUST override its state with this - no local computation
        return { travellers };
      } catch (error) {
        logger.error(`[TravellerThunk:${name}] Failed:`, error);
        if (error instanceof TravellerDetailsError) {
          return rejectWithValue(error.message);
        }
        return rejectWithValue(`Failed to ${name} traveller`);
      }
    },
    {
      // Prevent duplicate API calls while loading
      condition: (_, { getState }) => {
        const state = getState() as RootState;
        return !state.hotels.bookingReview.isTravellerOperationLoading;
      },
    },
  );
}

// ============================================
// Thunks
// ============================================

/**
 * Add a new traveller
 * Response contains updated list of ALL travellers
 */
export const addTravellerThunk = createTravellerThunk('add', 'hotel_traveller_detail_add');

/**
 * Edit an existing traveller
 * Response contains updated list of ALL travellers
 */
export const editTravellerThunk = createTravellerThunk('edit', 'hotel_traveller_detail_edit');

/**
 * Select/Deselect a traveller
 * Creates a thunk for toggling traveller selection
 */
export const toggleTravellerSelectionThunk = createAsyncThunk<
  TravellerOperationResult,
  SelectTravellerParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotels/travellers/toggleSelection',
  async (params, { getState, rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const userId = getUUID();
      const state = getState();

      // Get current traveller from state to send full details
      const currentTravellers = state.hotels.bookingReview.travellers;
      const traveller = currentTravellers.find((t) => t.travellerId === params.travellerId);

      if (!traveller) {
        return rejectWithValue('Traveller not found');
      }

      const actionType: TravellerDetailActionType = params.selected
        ? 'hotel_traveller_detail_select'
        : 'hotel_traveller_detail_deselect';

      logger.info('[TravellerThunk:toggleSelection] Starting operation', {
        travellerId: params.travellerId,
        selected: params.selected,
      });

      // Use provided roomNumber if specified, otherwise keep existing
      const roomNumber = params.roomNumber ?? traveller.roomNumber;

      const response = await callTravellerDetailsApi({
        sessionId,
        userId,
        transactionKey: params.transactionKey,
        actionType,
        travellerDetails: [
          {
            travellerId: traveller.travellerId,
            salutation: traveller.salutation,
            firstName: traveller.firstName,
            lastName: traveller.lastName,
            isPrimary: traveller.isPrimary,
            emailId: traveller.emailId,
            contactInfo: traveller.contactInfo,
            roomNumber,
            isSelected: params.selected,
          },
        ],
      });

      if (!response.success && response.error) {
        logger.warn('[TravellerThunk:toggleSelection] API error:', response.error);
        return rejectWithValue(response.error.message);
      }

      // TEMPORARY FALLBACK: If backend returns empty travellers, generate locally
      let travellers = response.travellers;
      if (travellers.length === 0) {
        logger.warn(
          '[TravellerThunk:toggleSelection] Backend returned empty travellers, using local fallback',
        );
        travellers = currentTravellers.map((t) => {
          if (t.travellerId === params.travellerId) {
            return {
              ...t,
              isSelected: params.selected,
              roomNumber: params.roomNumber ?? t.roomNumber,
            };
          }
          return t;
        });
      }

      logger.info('[TravellerThunk:toggleSelection] Success', {
        travellerCount: travellers.length,
      });

      // Return the complete traveller list from backend
      return { travellers };
    } catch (error) {
      logger.error('[TravellerThunk:toggleSelection] Failed:', error);
      if (error instanceof TravellerDetailsError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to update traveller selection');
    }
  },
  {
    // Prevent duplicate API calls while loading
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.hotels.bookingReview.isTravellerOperationLoading;
    },
  },
);
